import { getOrDefault } from 'libs/PagingParser'
import debounce from 'lodash.debounce'
import { first, compact, forEach as lodashForEach } from 'lodash'
import moment from 'moment'
import momentTimezone from 'moment-timezone'
import {
  __,
  add,
  and,
  compose,
  equals,
  filter,
  find,
  forEach,
  gt,
  has,
  includes,
  intersection,
  isEmpty,
  isNil,
  join,
  keys,
  length,
  lensPath,
  map,
  not,
  pathOr,
  prop,
  propEq,
  propOr,
  trim,
  union,
  view,
} from 'ramda'
import { useCallback, useReducer, useState } from 'react'
import { useFlag } from '@unleash/proxy-client-react'

import {
  TIMEOUT_SEARCH,
  TYPE_SEND_NOW,
  TYPE_SEND_SCHEDULE,
} from 'app/constants/compose'
import effects from 'app/effects/effects'
import { PlanId } from 'commons/plan'
import { useFormikContext } from 'formik'
import { API_DATE_FORMATTED } from 'libs/dateUtils'

import {
  LARGE_ROOM_ID,
  MEDIUM_ROOM_ID,
  OPEN_SPACE_ID,
  RANGE_3_VALUE,
  RANGE_5_VALUE,
  RANGE_7_VALUE,
  RANGE_8_VALUE,
  SMALL_ROOM_ID,
} from 'app/constants/beacon'
import {
  CHANNEL_APP,
  CHANNEL_CALL,
  CHANNEL_EMAIL,
  CHANNEL_SMS,
  FACEBOOK_VALUE,
  INSTAGRAM_VALUE,
  LINKEDIN_VALUE,
  TWITTER_VALUE,
} from 'app/constants/channel'
import { TYPE_EVENT, TYPE_GENERAL, TYPE_POLL } from 'app/constants/messageType'
import { SCOPE_BEACON, SCOPE_GENERAL } from 'app/constants/scope'
import { localStorageService } from 'app/services/localStorageService/localStorageService'
import { isEmptyOrNil } from 'libs/empty'

const TypeFile = {
  IMAGE_TYPE: 'image',
}

const minEventStart = moment().set({ second: 0, millisecond: 0 }).format()
const minEventEnd = moment(minEventStart)
  .add({ hour: 1 })
  .set({ second: 0, millisecond: 0 })
  .format()

const minSchedule = moment()
  .add(1, 'hours')
  .set({ second: 0, millisecond: 0 })
  .format()

const minReminder = moment()
  .add(1, 'hours')
  .set({ second: 0, millisecond: 0 })
  .format()

const maxDate = moment()
  .add(10, 'year')
  .set({ second: 0, millisecond: 0 })
  .format()

const minStartScheduleBeacon = moment()
  .set({ second: 0, millisecond: 0 })
  .format()

const minEndScheduleBeacon = moment(minStartScheduleBeacon)
  .add({ hour: 1 })
  .set({ second: 0, millisecond: 0 })
  .format()

const minEndPoll = moment()
  .add(1, 'hours')
  .set({ second: 0, millisecond: 0 })
  .format()

const getSocialChannelCount = (channels) => {
  try {
    const socialChannelWithoutPages = [TWITTER_VALUE, LINKEDIN_VALUE]
    const { channelIds, pageIds } = channels || {}
    const socialSelectedWithoutPagesCount = length(
      intersection(socialChannelWithoutPages, channelIds),
    )
    const socialSelectedWithPagesCount = length(pageIds)
    return socialSelectedWithoutPagesCount + socialSelectedWithPagesCount
  } catch (error) {
    return 0
  }
}

export const initialValuesForm = (place, initialModel) => {
  const isFreeTier = place.activeSubscription.braintreePlan === PlanId.FREE_TIER

  const initialTimezone =
    view(lensPath(['info', 'timezone']), place) || momentTimezone.tz.guess()

  const initialValues = {
    topics: getOrDefault([])(() =>
      filter(propEq('isSelected', true))(initialModel.topics),
    ),
    recipients: getOrDefault([])(() => initialModel.recipients),
    channels: getOrDefault({ channelIds: [], pageIds: [] })(() => {
      return {
        channelIds: initialModel.socialChannels.channels ?? [],
        pageIds: initialModel.socialChannels.pages.map(prop('id')) ?? [],
      }
    }),
    shortName: getOrDefault(undefined)(() => initialModel.shortName),
    title: getOrDefault(undefined)(() => initialModel.title),
    body: getOrDefault(undefined)(() => initialModel.body),
    location: {
      suggestion: getOrDefault(place.info.address)(
        () => initialModel.eventPlace.address,
      ),
      lng: getOrDefault(place.info.longitude)(
        () => initialModel.eventPlace.longitude,
      ),
      lat: getOrDefault(place.info.latitude)(
        () => initialModel.eventPlace.latitude,
      ),
      timeZoneId: getOrDefault(initialTimezone)(
        () => initialModel.eventPlace.timeZoneId,
      ),
    },
    allowConversation: getOrDefault(true)(() =>
      isFreeTier ? false : initialModel.allowConversation,
    ),
    isTtsEnabled: getOrDefault(true)(() =>
      isFreeTier ? false : initialModel.isTtsEnabled,
    ),
    isSharingEnabled: getOrDefault(false)(() => initialModel.isSharingEnabled),
    isDisplayHistory: getOrDefault(false)(() => initialModel.displayHistory),
    expirationTime: getOrDefault(0)(() => initialModel.expirationTime),
    notiType: getOrDefault(TYPE_GENERAL)(() => initialModel.notiType),
    priority: getOrDefault(1)(() => initialModel.priority),
    eventStartDateTime: getOrDefault(undefined)(
      () => initialModel.event.startDateTime,
    ),
    eventEndDateTime: getOrDefault(undefined)(
      () => initialModel.event.endDateTime,
    ),
    eventTimezone: getOrDefault(initialTimezone)(
      () => initialModel.event.startTimezone,
    ),

    voucher: getOrDefault(undefined)(() => initialModel.voucher?.id),
    mediaSocials: getOrDefault([])(() => initialModel.mediaSocials),
    sendOptions: getOrDefault(TYPE_SEND_NOW)(() => initialModel.sendOptions),
    schedule: getOrDefault(undefined)(() => initialModel.scheduleDateTime),
    scheduleTimezone: getOrDefault(initialTimezone)(
      () => initialModel.scheduleTimezone,
    ),
    reminderChecked: getOrDefault(false)(
      () => !isNil(initialModel.reminderDateTime),
    ),
    reminder: getOrDefault(undefined)(() => initialModel.reminderDateTime),
    subscriberTotal: getOrDefault(undefined)(
      () => initialModel.subscriberTotal,
    ),
    reminderTimezone: getOrDefault(initialTimezone)(
      () => initialModel.reminderTimezone,
    ),
  }
  return initialValues
}

export const actionMessageType = {
  SET_LOADING_TOPICS: 'SET_LOADING_TOPICS',
  SET_TOPICS_OPTION: 'SET_TOPICS_OPTION',
  SET_RECIPIENTS_OPTION: 'SET_RECIPIENTS_OPTION',
  SET_KEYWORD_SEARCH_RECIPIENT: 'SET_KEYWORD_SEARCH_RECIPIENT',
  SET_LOADING_RECIPIENT: 'SET_LOADING_RECIPIENT',
  SET_LOADING_SEARCH: 'SET_LOADING_SEARCH',
  SET_LOADING_SUBSCRIBERS_TOTAL: 'SET_LOADING_SUBSCRIBERS_TOTAL',

  SET_SEARCH_RECIPIENT: 'SET_SEARCH_RECIPIENT',

  SETTING_CHANNEL: 'SETTING_CHANNEL',

  DISABLED_FORM: 'DISABLED_FORM',
  ENABLED_FORM: 'ENABLED_FORM',
  ON_TITLE_CHANGE: 'ON_TITLE_CHANGE',
  SET_VOUCHERS_OPTION: 'SET_VOUCHERS_OPTION',

  OPEN_HELP_TAB: 'OPEN_HELP_TAB',
  TOGGLE_HELP_TAB: 'TOGGLE_HELP_TAB',
  TOGGLE_SETTING_TAB: 'TOGGLE_SETTING_TAB',
  CLOSE_HELP_TAB: 'CLOSE_HELP_TAB',
  SCHEDULE_TIMEZONE_CHANGE: 'SCHEDULE_TIMEZONE_CHANGE',
  SET_MAX_EVENT_DATE: 'SET_MAX_EVENT_DATE',
  SET_MIN_EVENT_END: 'SET_MIN_EVENT_END',
  REMINDER_TIMEZONE_CHANGE: 'REMINDER_TIMEZONE_CHANGE',
  NOTIFICATION_TYPE_CHANGE: 'NOTIFICATION_TYPE_CHANGE',
  UPDATE_MIN_DATE_REMINDER: 'UPDATE_MIN_DATE_REMINDER',
  UPDATE_SUBSCRIPTION_TOTAL: 'UPDATE_SUBSCRIPTION_TOTAL',
  CLOSR_CREDIT: 'CLOSR_CREDIT',
  OPEN_CREDIT: 'OPEN_CREDIT',
  ON_SCHEDULE_CHANGE: 'ON_SCHEDULE_CHANGE',
  CLOSE_RIGHT_TAB: 'CLOSE_RIGHT_TAB',
  HANDLE_RIGTH_TAB_MOBILE: 'HANDLE_RIGTH_TAB_MOBILE',
  OPEN_RIGHT_TAB_MOBILE: 'OPEN_RIGHT_TAB_MOBILE',
  CLOSE_RIGHT_TAB_MOBILE: 'CLOSE_RIGHT_TAB_MOBILE',
  SET_CAHNNEL_LOADING: 'SET_CAHNNEL_LOADING',
  SET_SCHEDULE_DIABLED: 'SET_SCHEDULE_DIABLED',
  SHOW_MESSAGE_RATIO_MESSAGE: 'SHOW_MESSAGE_RATIO_MESSAGE',
  AI_GENERATE_TEXT_UPDATED: 'AI_GENERATE_TEXT_UPDATED',
  SET_MIN_SCHEDULE_END_BEACON: 'SET_MIN_SCHEDULE_END_BEACON',
  SET_UPLOAD_IMAGE_CONFIG: 'SET_UPLOAD_IMAGE_CONFIG',
  SET_MAX_SCHEDULE: 'SET_MAX_SCHEDULE',
  SET_RECIPIENTS: 'SET_RECIPIENTS',
}

const reducer = (state, action) => {
  switch (action.type) {
    case actionMessageType.SET_LOADING_TOPICS:
      return {
        ...state,
        isLoadingTopics: true,
      }
    case actionMessageType.SET_TOPICS_OPTION:
      return {
        ...state,
        topicsOptions: action.value,
        isLoadingTopics: false,
      }
    case actionMessageType.SET_RECIPIENTS_OPTION:
      return {
        ...state,
        recipientsOptions: action.value,
        isLoadingRecipient: false,
      }
    case actionMessageType.SET_KEYWORD_SEARCH_RECIPIENT:
      return {
        ...state,
        keywordSearchRecipient: action.value,
      }
    case actionMessageType.SET_LOADING_RECIPIENT:
      return {
        ...state,
        isLoadingRecipient: action.value,
      }
    case actionMessageType.SET_LOADING_SEARCH:
      return {
        ...state,
        isLoadingSearch: action.value,
      }
    case actionMessageType.SET_SEARCH_RECIPIENT:
      return {
        ...state,
        searchRecipient: action.value,
        isLoadingSearch: false,
      }

    case actionMessageType.SETTING_CHANNEL:
      return {
        ...state,
        channelSetting: action.setting,
        subscriberTotal: action.subscriberTotal,
        isLoadingSubscriberTotal: false,
        isOpenCredit: action.isOpenCredit,
        updatedAt: moment().format(),
      }
    case actionMessageType.DISABLED_FORM:
      return {
        ...state,
        isFormValid: false,
      }
    case actionMessageType.ENABLED_FORM:
      return {
        ...state,
        isFormValid: true,
      }
    case actionMessageType.SET_VOUCHERS_OPTION:
      return {
        ...state,
        vouchersOptions: action.value,
      }
    case actionMessageType.OPEN_HELP_TAB:
      return {
        ...state,
        isShowHelpTab: true,
      }
    case actionMessageType.CLOSE_HELP_TAB:
      return {
        ...state,
        isShowHelpTab: false,
      }
    case actionMessageType.TOGGLE_HELP_TAB:
      return {
        ...state,
        isShowHelpTab: !state.isShowHelpTab,
        isShowSettingTab: false,
      }
    case actionMessageType.TOGGLE_SETTING_TAB:
      return {
        ...state,
        isShowSettingTab: !state.isShowSettingTab,
        isShowHelpTab: false,
      }
    case actionMessageType.SCHEDULE_TIMEZONE_CHANGE:
      return {
        ...state,
        minSchedule: action.minDate,
        maxSchedule: action.maxDate,
      }
    case actionMessageType.REMINDER_TIMEZONE_CHANGE:
      return {
        ...state,
        minReminder: action.minDate,
        maxReminder: action.maxDate,
      }
    case actionMessageType.SET_MIN_EVENT_END:
      return {
        ...state,
        minEndEvent: action.minEndEvent,
        maxSchedule: action.maxSchedule,
        isScheduleDisabled: action.isScheduleDisabled,
      }
    case actionMessageType.SET_MAX_EVENT_DATE:
      return {
        ...state,
        maxStartEvent: action.maxStartEvent,
        maxEndEvent: action.maxEndEvent,
      }
    case actionMessageType.NOTIFICATION_TYPE_CHANGE:
      return {
        ...state,
        isScheduleDisabled: false,
        maxSchedule: maxDate,
      }
    case actionMessageType.UPDATE_MIN_DATE_REMINDER:
      return {
        ...state,
        minReminder: action.minReminder,
      }
    case actionMessageType.SET_LOADING_SUBSCRIBERS_TOTAL:
      return {
        ...state,
        isLoadingSubscriberTotal: true,
      }
    case actionMessageType.UPDATE_SUBSCRIPTION_TOTAL:
      return {
        ...state,
        subscriberTotal: action.subscriberTotal,
        isLoadingSubscriberTotal: false,
        isOpenCredit: action.isOpenCredit,
        updatedAt: moment().format(),
      }
    case actionMessageType.CLOSR_CREDIT:
      return {
        ...state,
        isOpenCredit: false,
      }
    case actionMessageType.OPEN_CREDIT:
      return {
        ...state,
        isOpenCredit: true,
      }
    case actionMessageType.ON_SCHEDULE_CHANGE:
      return {
        ...state,
        minReminder: action.minReminder,
      }

    case actionMessageType.ON_CURRENT_DATE_CHANGE:
      return {
        ...state,
        minSchedule: action.minSchedule,
      }
    case actionMessageType.CLOSE_RIGHT_TAB:
      return {
        ...state,
        isShowHelpTab: false,
        isShowSettingTab: false,
      }
    case actionMessageType.HANDLE_RIGTH_TAB_MOBILE:
      return {
        ...state,
        isShowRigthTabMobile: !state.isShowRigthTabMobile,
        isShowHelpTab:
          !state.isShowRigthTabMobile === false
            ? false
            : state.isShowSettingTab,

        isShowSettingTab:
          !state.isShowRigthTabMobile === false
            ? false
            : state.isShowSettingTab,
      }
    case actionMessageType.SET_CAHNNEL_LOADING:
      return {
        ...state,
        isChannelLoading: action.payload,
      }
    case actionMessageType.SET_SCHEDULE_DIABLED:
      return {
        ...state,
        isScheduleDisabled: action.disabled,
      }
    case actionMessageType.SHOW_MESSAGE_RATIO_MESSAGE:
      return {
        ...state,
        isShowMessageRatioMessage: action?.isShowMessageRatioMessage || false,
      }
    case actionMessageType.AI_GENERATE_TEXT_UPDATED:
      return {
        ...state,
        aiGenerateTextUpdated: moment().format(),
      }
    case actionMessageType.SET_MIN_SCHEDULE_END_BEACON:
      return {
        ...state,
        minEndScheduleBeacon: action.minEndScheduleBeacon,
      }
    case actionMessageType.SET_UPLOAD_IMAGE_CONFIG:
      return {
        ...state,
        uploadImageConfig: action.value,
      }

    case actionMessageType.SET_MAX_SCHEDULE:
      return {
        ...state,
        maxSchedule: action.maxSchedule,
      }
    case actionMessageType.SET_RECIPIENTS:
      return {
        ...state,
        recipients: action.payload,
        isLoadingRecipient: false,
        recipientsOptions: action.payload,
      }

    default:
      return state
  }
}

const initialValue = {
  recipientsOptions: [],
  isFormValid: false,
  rederRecipient: [],
  subscriberTotal: {},
  channelSetting: undefined,
  keywordSearchRecipient: '',
  searchRecipient: [],
  isLoadingRecipient: true,
  isChannelLoading: false,
  isLoadingSearch: false,
  rederChannelsSocialMedia: [],
  isSubscriberInGroup: false,
  isSelectedChannel: false,
  isLoadingChannels: true,
  topicsOptions: [],
  vouchersOptions: [],
  isShowHelpTab: false,
  isShowSettingTab: false,
  event: undefined,
  isLoadingTopics: true,
  minStartEvent: minEventStart,
  maxStartEvent: maxDate,
  minEndEvent: minEventEnd,
  maxEndEvent: maxDate,
  minSchedule,
  maxSchedule: maxDate,
  minReminder,
  maxReminder: maxDate,
  isScheduleDisabled: false,
  isLoadingSubscriberTotal: false,
  isOpenCredit: false,
  isShowRigthTabMobile: !localStorageService.getComposeAITour(),
  updatedAt: undefined,
  isShowMessageRatioMessage: false,
  aiGenerateTextUpdated: undefined,
  minStartScheduleBeacon: minStartScheduleBeacon,
  maxStartScheduleBeacon: maxDate,
  minEndScheduleBeacon: minEndScheduleBeacon,
  maxEndScheduleBeacon: maxDate,
  uploadImageConfig: undefined,
  minEndPoll,
  maxEndPoll: maxDate,
}

// get subscriber for disabled or enabled channel option
export const fetchSubscriberTotalForSettingChannel = async (
  subGroupIds = [],
  subscriberIds = [],
  topics = [],
  scope,
) => {
  return await effects.apis
    .getSubscriberCount({
      subgroupIds: subGroupIds,
      subscriberIds: subscriberIds,
      topicIds: compose(join(','), map(prop('id')))(topics),
      scope,
    })
    .then((response) => {
      const setting = {
        app: response.applicationCount > 0,
        sms: response.smsCount > 0,
        email: response.emailCount > 0,
        call: response.automateCallCount > 0,
      }
      const subscriberTotal = {
        applicationCount: prop('applicationCount', response),
        automateCallCount: prop('automateCallCount', response),
        emailCount: prop('emailCount', response),
        smsCount: prop('smsCount', response),
        subscriberCount: prop('subscriberCount', response),
      }

      return { setting, subscriberTotal }
    })
}

export const useComposeMessage = (
  notification,
  isEditMode,
  isDraft,
  isBeaconDialog,
  isCopyMessage,
  editorRef,
) => {
  const formik = useFormikContext()
  const [state, dispatch] = useReducer(reducer, initialValue)
  const isSent = propOr(false, 'isSent', notification)
  const isEvent = propEq('notiType', TYPE_EVENT, notification)
  const startDateTime = pathOr(
    undefined,
    ['event', 'startDateTime'],
    notification,
  )
  const [socialChannelCountState, setSocialChannelCountState] = useState(0)
  const mes_5281_save_draft_for_polls = useFlag('mes_5281_save_draft_for_polls')

  const setValueOpenDialogOtherPage = async () => {
    if (!isEmptyOrNil(notification)) {
      const subGroupIds = notification.recipients.subGroups

      const channelIds = notification?.socialChannels?.channels || []
      const pageIds =
        notification?.socialChannels?.pages?.map((page) => page?.id) || []
      const socialChannelCount = getSocialChannelCount({ channelIds, pageIds })
      setSocialChannelCountState(socialChannelCount)
      const subscriberIds = notification.recipients.subscribers.map(
        (item) => item.id,
      )

      const { setting, subscriberTotal: subscriberTotalFromSetting } =
        await fetchSubscriberTotalForSettingChannel(
          subGroupIds,
          subscriberIds,
          [],
          isBeaconDialog ? SCOPE_BEACON : SCOPE_GENERAL,
        )

      const subscriberTotal = isNil(notification.channels)
        ? subscriberTotalFromSetting
        : await getSubscriberCountForReceive(
            notification.channels,
            notification.recipients,
          )

      const subscriberCount = subscriberTotal?.subscriberCount || 0
      const isShowMessageRatioMessage = !!add(
        subscriberCount,
        socialChannelCount,
      )
      dispatchShowMessageRatio(isShowMessageRatioMessage)

      await dispatch({
        type: actionMessageType.SETTING_CHANNEL,
        setting: setting,
        subscriberTotal: subscriberTotal,
        isOpenCredit: false,
      })

      const keysValue = keys(notification)

      if (keysValue.length > 0 && !isEditMode && !isDraft && !isCopyMessage) {
        forEach(async (key) => {
          await formik.setFieldValue([key], notification[key])
        }, keysValue)

        if (has('recipients', notification) && !has('channels', notification)) {
          const channelIds = []
          if (subscriberTotal.applicationCount > 0) {
            channelIds.push(CHANNEL_APP)
          }
          if (subscriberTotal.emailCount > 0) {
            channelIds.push(CHANNEL_EMAIL)
          }
          if (subscriberTotal.smsCount > 0) {
            channelIds.push(CHANNEL_SMS)
          }
          if (subscriberTotal.automateCallCount > 0) {
            channelIds.push(CHANNEL_CALL)
          }
          const isSharingEnabled = !(
            channelIds.length === 1 && includes(CHANNEL_APP, channelIds)
          )

          await formik.setFieldValue('isSharingEnabled', isSharingEnabled)
          await formik.setFieldValue('channels', {
            pageIds: [],
            channelIds,
          })
        }
      }
      if (isDraft || isEditMode) {
        formik.setFormikState((prevState) => {
          return {
            ...prevState,
            dirty: false,
          }
        })
      }
    }
  }

  const handleFirstMount = async () => {
    fetchTopics({ page: 1, limit: 999, status: 1 })
    fetchVouchers({ page: 1, limit: 999, status: 1, sort: 'name:ASC' })
    checkDisabledSchedule()
    await setValueOpenDialogOtherPage()
  }

  const disabledForm = () => {
    dispatch({ type: actionMessageType.DISABLED_FORM })
  }

  const enabledForm = () => {
    dispatch({ type: actionMessageType.ENABLED_FORM })
  }

  const dispatchShowMessageRatio = (isShowMessageRatioMessage) => {
    const actionType = actionMessageType.SHOW_MESSAGE_RATIO_MESSAGE
    dispatch({
      type: actionType,
      isShowMessageRatioMessage,
    })
  }

  /**
   * Fetch API => /vouchers >>
   */
  const fetchVouchers = useCallback((params) => {
    effects.apis.getDealsPromotionList(params).then((response) => {
      dispatch({
        type: actionMessageType.SET_VOUCHERS_OPTION,
        value: response.data,
      })
    })
  }, [])

  /**
   * Fetch API => /topics >>
   */
  const fetchTopics = useCallback((params) => {
    effects.apis.getTopicList(params).then((response) => {
      dispatch({
        type: actionMessageType.SET_TOPICS_OPTION,
        value: response.data,
      })
    })
  }, [])

  const checkDisabledSchedule = useCallback(() => {
    let disabled = false

    if ((isEditMode || isDraft) && isEvent) {
      const isEventToday = moment().isSameOrAfter(moment(startDateTime), 'day')
      if (isEventToday) {
        disabled = true
      }
    }

    if (isEditMode && isEvent && isSent) {
      disabled = true
    }

    dispatch({ type: actionMessageType.SET_SCHEDULE_DIABLED, disabled })
  }, [startDateTime, isDraft, isEditMode, isEvent, isSent])

  /**
   * Fetch API => /search-recipient >>
   */

  const fetchSearchRecipient = useCallback(
    debounce((value, scope) => {
      if (!isEmpty(value)) {
        dispatch({
          type: actionMessageType.SET_LOADING_SEARCH,
          value: true,
        })
        effects.apis.searchRecipient(value, scope).then((response) => {
          dispatch({
            type: actionMessageType.SET_SEARCH_RECIPIENT,
            value: response,
          })
        })
      }
    }, TIMEOUT_SEARCH),
    [],
  )

  /**
   * keyword for Search Recipient >>
   */
  const onChangeSearchRecipient = useCallback(
    (value, isBeacon) => {
      dispatch({
        type: actionMessageType.SET_KEYWORD_SEARCH_RECIPIENT,
        value: trim(value),
      })
      fetchSearchRecipient(trim(value), isBeacon ? SCOPE_BEACON : SCOPE_GENERAL)
    },
    [fetchSearchRecipient],
  )

  // get subscriber for show sobscriber count
  const getSubscriberCountForReceive = useCallback(
    (channels = [], recipients) => {
      const subGroupIds = recipients.subGroups
      const subscriberIds = formik.values.recipients.subscribers.map(
        (item) => item.id,
      )
      const topics = formik.values.topics
      const isEmptySubscribers = and(
        isEmpty(subGroupIds),
        isEmpty(subscriberIds),
      )

      if (!isEmptySubscribers) {
        return effects.apis
          .getSubscriberCountForReceive({
            subgroupIds: subGroupIds,
            subscriberIds: subscriberIds,
            topicIds: compose(join(','), map(prop('id')))(topics),
            channels: channels,
          })
          .then((response) => {
            const totalCounted = {
              applicationCount: prop('app', response),
              automateCallCount: prop('automateCall', response),
              emailCount: prop('email', response),
              smsCount: prop('sms', response),
              subscriberCount: prop('count', response),
            }

            return totalCounted
          })
      } else {
        return {}
      }
    },
    [formik.values.recipients, formik.values.topics],
  )

  const onOpenHelpTab = () => {
    dispatch({
      type: actionMessageType.OPEN_HELP_TAB,
    })
  }

  const onCloseHelpTab = () => {
    dispatch({
      type: actionMessageType.CLOSE_HELP_TAB,
    })
  }

  const toggleHelpTab = () => {
    dispatch({
      type: actionMessageType.TOGGLE_HELP_TAB,
    })
  }

  const toggleSettingTab = () => {
    dispatch({
      type: actionMessageType.TOGGLE_SETTING_TAB,
    })
  }

  const onCloseRightTab = () => {
    dispatch({
      type: actionMessageType.CLOSE_RIGHT_TAB,
    })
  }

  // Helper function to convert datetime when timezone changes
  const convertDateTimeForTimezone = (
    dateValue,
    previousTimezone,
    newTimezone,
  ) => {
    if (!dateValue) return null

    const dateInPreviousTz = momentTimezone(dateValue)
      .tz(previousTimezone)
      .format(API_DATE_FORMATTED)

    return momentTimezone.tz(dateInPreviousTz, newTimezone).utc().format()
  }

  // Unified timezone change handler - updates all timezone fields
  const onUnifiedTimezoneChange = async (timezone) => {
    // Get the current timezone (should be the same across all fields, but get the first available)
    const timezoneFields = [
      formik.values?.scheduleTimezone,
      formik.values?.reminderTimezone,
      formik.values?.eventTimezone,
      formik.values?.pollTimezone,
    ]
    const previousTimezone =
      first(compact(timezoneFields)) || momentTimezone.tz.guess()

    // Read datetime values before updating formik.values
    const currentDateTimeValues = {
      schedule: formik.values.schedule,
      reminder: formik.values.reminder,
      eventStartDateTime: formik.values.eventStartDateTime,
      eventEndDateTime: formik.values.eventEndDateTime,
      pollEndDateTime: formik.values.pollEndDateTime,
    }

    // Convert all datetime fields using the helper function
    const dateTimeUpdates = {}
    const dateTimeFields = [
      { field: 'schedule', value: currentDateTimeValues.schedule },
      { field: 'reminder', value: currentDateTimeValues.reminder },
      {
        field: 'eventStartDateTime',
        value: currentDateTimeValues.eventStartDateTime,
      },
      {
        field: 'eventEndDateTime',
        value: currentDateTimeValues.eventEndDateTime,
      },
      {
        field: 'pollEndDateTime',
        value: currentDateTimeValues.pollEndDateTime,
      },
    ]

    dateTimeFields.forEach(({ field, value }) => {
      const convertedDate = convertDateTimeForTimezone(
        value,
        previousTimezone,
        timezone,
      )
      if (convertedDate) {
        dateTimeUpdates[field] = convertedDate
      }
    })

    // Update all timezone fields and converted datetime fields in a single batched update
    formik.setValues(
      {
        ...formik.values,
        scheduleTimezone: timezone,
        reminderTimezone: timezone,
        eventTimezone: timezone,
        pollTimezone: timezone,
        ...dateTimeUpdates,
      },
      false,
    )

    // Update reminder min date if schedule exists
    if (formik.values.schedule) {
      const minDate = momentTimezone
        .tz(formik.values.schedule, timezone)
        .add({ hours: 1 })
        .format()

      dispatch({
        type: actionMessageType.UPDATE_MIN_DATE_REMINDER,
        minReminder: moment(minDate).format(),
      })
    }

    momentTimezone.tz.setDefault(timezone)
  }

  const onScheduleTimezoneChange = (timezone) => {
    onUnifiedTimezoneChange(timezone)
  }

  async function onContentGeneratedWithAI(title, body) {
    await Promise.all([
      formik.setFieldValue('title', title),
      dispatch({ type: actionMessageType.AI_GENERATE_TEXT_UPDATED }),
    ])
  }

  const onMessageOptionChange = async (optionType) => {
    momentTimezone.tz.setDefault(formik.values.scheduleBeaconTimezone)

    if (optionType === TYPE_SEND_SCHEDULE) {
      if (
        formik.values.notiType === TYPE_POLL &&
        !isNil(formik.values.pollEndDateTime)
      ) {
        let date = momentTimezone()
          .tz(formik.values.scheduleTimezone)
          .add({ days: 7 })
          .set({ hour: 8, minute: 0, second: 0 })
          .format(API_DATE_FORMATTED)
        if (formik.values.pollEndDateTime) {
          date = momentTimezone
            .tz(formik.values.pollEndDateTime, formik.values.pollTimezone)
            .subtract({ hours: 1 })
            .format(API_DATE_FORMATTED)
        }
        dispatch({
          type: actionMessageType.SET_MAX_SCHEDULE,
          maxSchedule: moment(date).add({ hour: 1 }).format(),
        })
        await formik.setFieldValue('schedule', date)
      } else {
        let date = momentTimezone()
          .tz(formik.values.scheduleBeaconTimezone)
          .add({ days: 7 })
          .set({ hour: 8, minute: 0, second: 0 })
          .format(API_DATE_FORMATTED)
        if (formik.values.eventStartDateTime) {
          date = momentTimezone
            .tz(
              formik.values.eventStartDateTime,
              formik.values.scheduleBeaconTimezone,
            )
            .subtract({ hours: 1 })
            .format(API_DATE_FORMATTED)
        }
        dispatch({
          type: actionMessageType.UPDATE_MIN_DATE_REMINDER,
          minReminder: moment(date).add({ hour: 1 }).format(),
        })
        await formik.setFieldValue('schedule', date)
      }
    } else {
      dispatch({
        type: actionMessageType.UPDATE_MIN_DATE_REMINDER,
        minReminder,
      })
      await formik.setFieldValue('schedule', undefined)
    }
  }

  const onEventStartDateChange = async (date) => {
    const minEndEvent = moment(
      momentTimezone.tz(date, formik.values.eventTimezone).format(),
    ).format()

    const isScheduleDisabled =
      isSent && !isDraft
        ? true
        : momentTimezone(date).isSame(momentTimezone(), 'day')
    const maxSchedule = minEndEvent

    dispatch({
      type: actionMessageType.SET_MIN_EVENT_END,
      minEndEvent,
      maxSchedule: maxSchedule,
      isScheduleDisabled,
    })

    if (isScheduleDisabled) {
      await formik.setFieldValue('sendOptions', TYPE_SEND_NOW)
      await formik.setFieldValue('schedule', undefined)
    }
  }

  const onScheduleStartDateChange = (date) => {
    const minEndScheduleBeacon = moment(
      momentTimezone.tz(date, formik.values.scheduleBeaconTimezone).format(),
    ).format()

    dispatch({
      type: actionMessageType.SET_MIN_SCHEDULE_END_BEACON,
      minEndScheduleBeacon,
    })
  }

  const onEndDateChange = async (date) => {
    if ([undefined, null].includes(formik.values.eventEndDateTime)) {
      await formik.setFieldValue(
        'eventEndDateTime',
        moment(date).add({ hour: 1 }).utc().format(),
      )
    }
  }

  const onEventTimezoneChange = async (timezone) => {
    onUnifiedTimezoneChange(timezone)
  }

  const onScheduleBeaconTimezoneChange = async (timezone) => {
    if (formik.values.scheduleStartDate) {
      const scheduleStartDate = momentTimezone(formik.values.scheduleStartDate)
        .tz(formik.values.scheduleBeaconTimezone)
        .format(API_DATE_FORMATTED)

      const newScheduleStartDate = momentTimezone
        .tz(scheduleStartDate, timezone)
        .utc()
        .format()

      await formik.setFieldValue('scheduleStartDate', newScheduleStartDate)
    }
    if (formik.values.scheduleEndDate) {
      const scheduleEndDate = momentTimezone(formik.values.scheduleEndDate)
        .tz(formik.values.scheduleBeaconTimezone)
        .format(API_DATE_FORMATTED)

      const newScheduleEndDate = momentTimezone
        .tz(scheduleEndDate, timezone)
        .utc()
        .format()

      await formik.setFieldValue('scheduleEndDate', newScheduleEndDate)
    }
    momentTimezone.tz.setDefault(timezone)
  }

  const onVoucherChange = (voucher) => {
    dispatch({
      type: actionMessageType.SET_MAX_EVENT_DATE,
      maxStartEvent: isNil(voucher) ? maxDate : voucher.endDate,
      maxEndEvent: isNil(voucher) ? maxDate : voucher.endDate,
    })
  }

  const onCheckedReminder = (event) => {
    const checked = event.target.checked
    const newDate =
      formik.values.sendOptions === TYPE_SEND_SCHEDULE
        ? momentTimezone
            .tz(formik.values.schedule, formik.values.scheduleTimezone)
            .add({ hours: 1 })
            .format()
        : momentTimezone
            .tz(formik.values.reminderTimezone)
            .add({ hours: 1 })
            .format()

    formik.setFieldValue(
      'reminder',
      checked ? moment(newDate).format(API_DATE_FORMATTED) : undefined,
    )
    formik.setFieldValue('reminderChecked', checked)
  }

  const onNotificationTypeChange = async (type) => {
    if (includes(type, [TYPE_GENERAL, TYPE_POLL])) {
      await formik.setFieldValue('isTtsEnabled', type !== TYPE_POLL)
      if (type === TYPE_POLL) {
        const channelWithoutAutomateCall =
          formik.values.channels.channelIds.filter(
            (item) => item !== CHANNEL_CALL,
          )
        await formik.setFieldValue('channels', {
          ...formik.values.channels,
          channelIds: channelWithoutAutomateCall,
        })
        await formik.setFieldValue('reminder', undefined)
        await formik.setFieldValue('reminderChecked', false)
        if (mes_5281_save_draft_for_polls) {
          await formik.setFieldValue('pollEndDateTime', '')
        }
      }

      dispatch({ type: actionMessageType.NOTIFICATION_TYPE_CHANGE })
    } else {
      await formik.setFieldValue('isTtsEnabled', true)
      await formik.setFieldValue('reminder', undefined)
      await formik.setFieldValue('reminderChecked', false)
    }
    await formik.validateForm()
  }

  const mapChannels = (channelSetting, channels, isBeaconDialog) => {
    let channelIds = []
    let pageIds = channels.pageIds

    if (channelSetting.app) channelIds.push(CHANNEL_APP)
    if (channelSetting.sms) channelIds.push(CHANNEL_SMS)
    if (channelSetting.email) channelIds.push(CHANNEL_EMAIL)
    if (
      channelSetting.call &&
      formik.values.notiType !== TYPE_POLL &&
      !isBeaconDialog
    )
      channelIds.push(CHANNEL_CALL)

    const socialChannel = filter(
      includes(__, [
        FACEBOOK_VALUE,
        TWITTER_VALUE,
        LINKEDIN_VALUE,
        INSTAGRAM_VALUE,
      ]),
      channels.channelIds,
    )

    return { channelIds: union(socialChannel, channelIds), pageIds }
  }

  const setEnabledTts = (channels) => {
    const isPoll = formik.values.notiType === TYPE_POLL
    formik.setFieldValue(
      'isTtsEnabled',
      isPoll ? false : includes(CHANNEL_CALL, channels),
    )
  }

  const handleSharingEnabled = ({ channelIds, pageIds }) => {
    const hasSharing = compose(
      gt(__, 0),
      length,
      filter(
        includes(__, [
          CHANNEL_EMAIL,
          CHANNEL_SMS,
          TWITTER_VALUE,
          LINKEDIN_VALUE,
        ]),
      ),
    )(channelIds)

    const isSharingEnabled = !isEmpty(pageIds) || hasSharing
    formik.setFieldValue('isSharingEnabled', isSharingEnabled)
    if (!isSharingEnabled) formik.setFieldValue('isDisplayHistory', false)
  }

  const onRecipientChange = async (recipients) => {
    const topics = formik.values.topics

    const channelIds = formik.values.channels.channelIds.filter((item) => {
      return formik.values.notiType === TYPE_POLL ? item !== CHANNEL_CALL : true
    })
    const channels = { ...formik.values.channels, channelIds }

    const subGroupIds = recipients.subGroups
    const subscriberIds = recipients.subscribers.map((item) => item.id)

    dispatch({
      type: actionMessageType.SET_LOADING_SUBSCRIBERS_TOTAL,
    })

    const channelSetting = await fetchSubscriberTotalForSettingChannel(
      subGroupIds,
      subscriberIds,
      topics,
      isBeaconDialog ? SCOPE_BEACON : SCOPE_GENERAL,
    )

    const subscriberTotal = channelSetting?.subscriberTotal || {}
    const subscriberCount = subscriberTotal?.subscriberCount || 0
    const isShowMessageRatioMessage = !!add(
      subscriberCount,
      socialChannelCountState,
    )
    dispatchShowMessageRatio(isShowMessageRatioMessage)
    await formik.setFieldValue('subscriberTotal', {
      ...subscriberTotal,
      socialChannelCount: socialChannelCountState,
    })

    dispatch({
      type: actionMessageType.SETTING_CHANNEL,
      setting: channelSetting.setting,
      subscriberTotal: channelSetting.subscriberTotal,
      isOpenCredit: false,
    })
    const matchChannels =
      recipients.length === 0
        ? {
            channelIds: filter(
              includes(__, [
                FACEBOOK_VALUE,
                INSTAGRAM_VALUE,
                TWITTER_VALUE,
                LINKEDIN_VALUE,
              ]),
              channels.channelIds,
            ),
            pageIds: channels.pageIds,
          }
        : mapChannels(channelSetting.setting, channels, isBeaconDialog)

    if (!isBeaconDialog) {
      handleSharingEnabled({
        channelIds: matchChannels.channelIds,
        pageIds: matchChannels.pageIds,
      })
    }

    includes(CHANNEL_CALL, matchChannels.channelIds) &&
      setEnabledTts(matchChannels.channelIds)

    await formik.setFieldValue('channels', matchChannels)
  }

  const onTopicsChange = async (topics = []) => {
    const channels = formik.values.channels
    const recipients = formik.values.recipients
    const subGroupIds = recipients.subGroups
    const subscriberIds = recipients.subscribers.map((item) => item.id)

    dispatch({
      type: actionMessageType.SET_LOADING_SUBSCRIBERS_TOTAL,
    })

    const channelSetting = await fetchSubscriberTotalForSettingChannel(
      subGroupIds,
      subscriberIds,
      topics,
      isBeaconDialog ? SCOPE_BEACON : SCOPE_GENERAL,
    )

    const subscriberTotal = channelSetting?.subscriberTotal || {}
    const subscriberCount = subscriberTotal?.subscriberCount || 0
    const isShowMessageRatioMessage = !!add(
      subscriberCount,
      socialChannelCountState,
    )
    dispatchShowMessageRatio(isShowMessageRatioMessage)
    formik.setFieldValue('subscriberTotal', {
      ...subscriberTotal,
      socialChannelCount: socialChannelCountState,
    })

    dispatch({
      type: actionMessageType.SETTING_CHANNEL,
      setting: channelSetting.setting,
      subscriberTotal: channelSetting.subscriberTotal,
      isOpenCredit: false,
    })

    const channelIds = filter(
      includes(__, [
        FACEBOOK_VALUE,
        TWITTER_VALUE,
        LINKEDIN_VALUE,
        INSTAGRAM_VALUE,
      ]),
      channels.channelIds,
    )
    const matchChannels =
      recipients.length === 0
        ? { channelIds: channelIds, pageIds: channels.pageIds }
        : mapChannels(channelSetting.setting, channels)

    if (!isBeaconDialog) {
      handleSharingEnabled({
        channelIds: matchChannels.channelIds,
        pageIds: matchChannels.pageIds,
      })
    }

    formik.setFieldValue('channels', matchChannels)
    includes(CHANNEL_CALL, matchChannels.channelIds) &&
      setEnabledTts(matchChannels.channelIds)
  }

  const onChannelsChange = async (value) => {
    const socialChannelCount = getSocialChannelCount(value)
    setSocialChannelCountState(socialChannelCount)
    dispatch({
      type: actionMessageType.SET_LOADING_SUBSCRIBERS_TOTAL,
    })
    if (!isEmpty(value.channelIds)) {
      const subscriberTotal = await getSubscriberCountForReceive(
        value.channelIds,
        formik.values.recipients,
      )
      const isShowMessageRatioMessage = !!add(
        subscriberTotal?.subscriberCount || 0,
        socialChannelCount,
      )
      dispatchShowMessageRatio(isShowMessageRatioMessage)
      formik.setFieldValue('subscriberTotal', {
        ...subscriberTotal,
        socialChannelCount,
      })

      dispatch({
        type: actionMessageType.UPDATE_SUBSCRIPTION_TOTAL,
        subscriberTotal,
        isOpenCredit: false,
      })
    } else {
      const isShowMessageRatioMessage = !!socialChannelCount
      dispatchShowMessageRatio(isShowMessageRatioMessage)
      formik.setFieldValue('subscriberTotal', {})
      dispatch({
        type: actionMessageType.UPDATE_SUBSCRIPTION_TOTAL,
        subscriberTotal: {},
        isOpenCredit: false,
      })
    }
  }

  const onSharingEnabledChange = async (checked) => {
    if (!checked) {
      const channels = formik.values.channels
      let channelIds = []
      const hasAutomateCall = compose(
        not,
        isNil,
        find((item) => {
          return item === CHANNEL_CALL
        }),
      )(channels.channelIds)

      if (hasAutomateCall) {
        channelIds.push(CHANNEL_CALL)
      }
      const hasAppChannel = compose(
        not,
        isNil,
        find((item) => {
          return item === CHANNEL_APP
        }),
      )(channels.channelIds)
      if (hasAppChannel) {
        channelIds.push(CHANNEL_APP)
      }
      await formik.setFieldValue('isDisplayHistory', false)
      const channelPayload = {
        channelIds,
        pageIds: [],
      }
      await formik.setFieldValue('channels', channelPayload)

      dispatch({
        type: actionMessageType.SET_LOADING_SUBSCRIBERS_TOTAL,
      })
      if (!isEmpty(channelIds)) {
        const subscriberTotal = await getSubscriberCountForReceive(
          channelIds,
          formik.values.recipients,
        )
        const socialChannelCount = getSocialChannelCount(channelPayload)
        const subscriberCount = subscriberTotal?.subscriberCount || 0
        const isShowMessageRatioMessage = !!add(
          subscriberCount,
          socialChannelCount,
        )
        dispatchShowMessageRatio(isShowMessageRatioMessage)
        formik.setFieldValue('subscriberTotal', {
          ...subscriberTotal,
          socialChannelCount,
        })
        dispatch({
          type: actionMessageType.UPDATE_SUBSCRIPTION_TOTAL,
          subscriberTotal,
          isOpenCredit: false,
        })
      } else {
        dispatch({
          type: actionMessageType.UPDATE_SUBSCRIPTION_TOTAL,
          subscriberTotal: {},
          isOpenCredit: false,
        })
        formik.setFieldValue('subscriberTotal', {})
        const hideMessageRatioMessage = false
        dispatchShowMessageRatio(hideMessageRatioMessage)
      }
    }
  }

  const onHandleTtsdisabled = async () => {
    if (includes(CHANNEL_CALL, formik.values.channels.channelIds)) {
      const channels = formik.values.channels.channelIds.filter(
        (channel) => !equals(CHANNEL_CALL, channel),
      )
      const page = formik.values.channels.pageIds

      const channelPayload = {
        channelIds: channels,
        pageIds: page,
      }
      formik.setFieldValue('channels', channelPayload)
      dispatch({
        type: actionMessageType.SET_LOADING_SUBSCRIBERS_TOTAL,
      })

      const subscriberTotal = await getSubscriberCountForReceive(
        channels,
        formik.values.recipients,
      )
      const socialChannelCount = getSocialChannelCount(channelPayload)
      const subscriberCount = subscriberTotal?.subscriberCount || 0
      const isShowMessageRatioMessage = !!add(
        subscriberCount,
        socialChannelCount,
      )
      dispatchShowMessageRatio(isShowMessageRatioMessage)
      formik.setFieldValue('subscriberTotal', {
        ...subscriberTotal,
        socialChannelCount,
      })
      dispatch({
        type: actionMessageType.UPDATE_SUBSCRIPTION_TOTAL,
        subscriberTotal,
        isOpenCredit: false,
      })
    }
  }

  const onBodyInsertImage = async (prevImageInfo, imageInfo) => {
    if (isNil(imageInfo)) {
      return
    }

    const getFileGif = imageInfo.path.match('.gif')

    if (isNil(getFileGif)) {
      const image = {
        id: (Math.random() * 100).toString(36).replace('.', ''),
        url: imageInfo.path,
        name: imageInfo.name,
        isSelected: isEmpty(prevImageInfo),
        type: TypeFile.IMAGE_TYPE,
      }

      const mapPrevImageInfo = await map((image) => {
        return {
          id: (Math.random() * 100).toString(36).replace('.', ''),
          url: image.path,
          name: image.name,
          isSelected: image.isSelected,
          type: TypeFile.IMAGE_TYPE,
        }
      }, prevImageInfo)
      await formik.setFieldValue('mediaSocials', [...mapPrevImageInfo, image])
    }
  }

  const onExpirationChange = async (value) => {
    if (value > 0) {
      await formik.setFieldValue('reminderChecked', false)
      await formik.setFieldValue('reminder', undefined)
    }
    await formik.setFieldValue('expirationTime', value)
  }

  const closeCredit = () => {
    dispatch({ type: actionMessageType.CLOSR_CREDIT })
  }

  const checkSocialChannelOnly = (channelIds) => {
    const socialChannel = filter(
      includes(__, [
        FACEBOOK_VALUE,
        TWITTER_VALUE,
        INSTAGRAM_VALUE,
        LINKEDIN_VALUE,
      ]),
      channelIds,
    )

    return socialChannel.length === channelIds.length
  }

  const onScheduleChange = (date) => {
    if (formik.values.notiType !== TYPE_POLL) {
      const minReminder = momentTimezone
        .tz(date, formik.values.scheduleTimezone)
        .add({ hours: 1 })
        .format()
      dispatch({
        type: actionMessageType.UPDATE_MIN_DATE_REMINDER,
        minReminder: moment(minReminder).format(),
      })
    }
  }

  async function onReplaceWithAIVersionClick({ revisedVersion }) {
    editorRef.current.commands.setContent(revisedVersion, true)
  }

  const toggleRightTabMobile = () => {
    dispatch({ type: actionMessageType.HANDLE_RIGTH_TAB_MOBILE })
  }

  const setChannelLoading = (loading) => {
    dispatch({ type: actionMessageType.SET_CAHNNEL_LOADING, payload: loading })
  }

  const openCreditDialog = () => {
    dispatch({ type: actionMessageType.OPEN_CREDIT })
  }

  const onRoomSizeChange = (value) => {
    formik.setFieldValue('roomSize', value)
    switch (value) {
      case SMALL_ROOM_ID:
        formik.setFieldValue('range', RANGE_3_VALUE)
        break
      case MEDIUM_ROOM_ID:
        formik.setFieldValue('range', RANGE_5_VALUE)
        break
      case LARGE_ROOM_ID:
        formik.setFieldValue('range', RANGE_7_VALUE)
        break
      case OPEN_SPACE_ID:
        formik.setFieldValue('range', RANGE_8_VALUE)
        break

      default:
        break
    }
  }

  const onRangeChange = (value) => {
    formik.setFieldValue('range', value)
    formik.setFieldValue('roomSize', '')
  }

  const onAddEndDateSchedule = async (event) => {
    const isChecked = event.target.checked
    if (!isChecked) {
      await formik.setFieldValue('scheduleEndDate', '')
    }
    await formik.setFieldTouched('scheduleEndDate', true)
    await formik.setFieldValue('addEndDateSchedule', isChecked)
  }

  const onScheduleEndDateChange = async (value) => {
    await formik.setFieldValue('scheduleEndDate', value)
  }

  const onPollEndChange = async (date) => {
    const maxSchedule = moment(
      momentTimezone.tz(date, formik.values.scheduleTimezone).format(),
    ).format()

    dispatch({
      type: actionMessageType.SET_MAX_SCHEDULE,
      maxSchedule,
    })

    if (moment().isSame(moment(date), 'day')) {
      await formik.setFieldValue('sendOptions', TYPE_SEND_NOW)
      await formik.setFieldValue('schedule', undefined)
    }
  }

  return {
    state,
    formik,
    handleFirstMount,
    onChangeSearchRecipient,
    disabledForm,
    enabledForm,
    fetchTopics,
    fetchVouchers,
    onOpenHelpTab,
    onCloseHelpTab,
    onScheduleTimezoneChange,
    onMessageOptionChange,
    onEventStartDateChange,
    onEventTimezoneChange,
    onEndDateChange,
    onVoucherChange,
    onCheckedReminder,
    onNotificationTypeChange,
    onRecipientChange,
    onTopicsChange,
    onChannelsChange,
    onSharingEnabledChange,
    onHandleTtsdisabled,
    onBodyInsertImage,
    onExpirationChange,
    closeCredit,
    checkSocialChannelOnly,
    onScheduleChange,
    onReplaceWithAIVersionClick,
    toggleHelpTab,
    toggleSettingTab,
    onCloseRightTab,
    toggleRightTabMobile,
    setChannelLoading,
    openCreditDialog,
    onRoomSizeChange,
    onRangeChange,
    onAddEndDateSchedule,
    onScheduleStartDateChange,
    onScheduleBeaconTimezoneChange,
    dispatchShowMessageRatio,
    onScheduleEndDateChange,
    onPollEndChange,
    onContentGeneratedWithAI,
  }
}
